import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Badge } from "@nimit9/signet-ui/components/badge"
import { Button } from "@nimit9/signet-ui/components/button"
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@nimit9/signet-ui/components/card"
import { EmptyState } from "@nimit9/signet-ui/components/empty-state"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { RatioFill, RatioProgress, RatioText } from "@nimit9/signet-ui/components/privacy-mask"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import { Tabs, TabsList, TabsTrigger } from "@nimit9/signet-ui/components/tabs"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"
import {
  AXIS_PROPS,
  BAR_CURSOR,
  BAR_RADIUS,
  CategoryLegend,
  CHART_MARGIN,
  ChartCard,
  type ChartConfig,
  ChartFrame,
  ChartTooltip,
  formatMonthShort,
  GRID_PROPS,
  legendItemsFromConfig,
  MoneyTooltip,
  PhonePeriodPills,
  Y_AXIS_WIDTH,
} from "@/components/charts"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useAnalytics } from "@/hooks/use-analytics"
import { useCompactCurrencyFormat } from "@/hooks/use-compact-currency-format"
import { useOwnerLabels } from "@/hooks/use-owner-labels"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { cn } from "@/lib/cn"
import { CATEGORY_COLORS } from "@/lib/constants"
import { buildAnalyticsHeadline } from "./-headline"

export const Route = createFileRoute("/analytics/")({
  component: Analytics,
})

const SPEND_CONFIG = {
  needs: { label: "Needs", color: CATEGORY_COLORS.needs },
  wants: { label: "Wants", color: CATEGORY_COLORS.wants },
  investments: { label: "Investments", color: CATEGORY_COLORS.investments },
} satisfies ChartConfig

const SPEND_FALLBACK_CONFIG = {
  spend: { label: "Spend", color: CATEGORY_COLORS.wants },
} satisfies ChartConfig

// Expense bars are neutral ink (debits are never red — PAISA-AMT-02); income
// takes the credit accent.
const IO_CONFIG = {
  income: { label: "Income", color: "var(--cat-income)" },
  expense: { label: "Expense", color: "var(--text-muted)" },
} satisfies ChartConfig

const SPEND_KEYS = ["needs", "wants", "investments"] as const

/** Savings-rate bands shared with Reports: ≥ 30% good, < 15% at risk (amber), else neutral. */
function rateTone(rate: number): "good" | "warn" | "muted" {
  return rate >= 30 ? "good" : rate < 15 ? "warn" : "muted"
}

const RATE_CHIP: Record<ReturnType<typeof rateTone>, string> = {
  good: "bg-accent-green-subtle text-accent-green",
  warn: "bg-accent-amber-subtle text-accent-amber",
  muted: "bg-bg-elevated text-text-secondary",
}

function AnalyticsSkeleton() {
  return (
    <div className="flex flex-col gap-4" data-testid="analytics-skeleton">
      <Skeleton className="h-28 animate-pulse ring-1 ring-border-subtle" />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-24 animate-pulse ring-1 ring-border-subtle" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Skeleton className="h-72 animate-pulse ring-1 ring-border-subtle" />
        <Skeleton className="h-72 animate-pulse ring-1 ring-border-subtle" />
      </div>
    </div>
  )
}

/** MoM change, coloured by meaning: a rise in spending is amber (at risk), never red. */
function Change({ pct, invert }: { pct: number | null; invert: boolean }) {
  if (pct === null) return <span className="text-caption text-text-muted">—</span>
  const worse = invert ? pct > 0 : pct < 0
  return (
    <span
      className={cn(
        "text-caption font-medium tabular-nums",
        pct === 0 ? "text-text-muted" : worse ? "text-accent-amber" : "text-accent-green",
      )}
    >
      <RatioText>{`${pct > 0 ? "+" : ""}${pct}%`}</RatioText> {pct > 0 ? "↑" : pct < 0 ? "↓" : ""}
    </span>
  )
}

function SectionCard({
  title,
  action,
  children,
  ...props
}: {
  title: string
  action?: React.ReactNode
  children: React.ReactNode
} & React.ComponentProps<"div">) {
  return (
    <Card className="min-w-0 gap-0 py-0" {...props}>
      <CardHeader className="border-b border-border-subtle px-4 py-3 md:px-5">
        <CardTitle className="text-callout font-semibold text-text-primary">{title}</CardTitle>
        {action && <CardAction>{action}</CardAction>}
      </CardHeader>
      <CardContent className="px-2 py-2 md:px-3">{children}</CardContent>
    </Card>
  )
}

function Analytics() {
  const ownerLabels = useOwnerLabels()
  const fmtCompact = useCompactCurrencyFormat()
  const navigate = useNavigate()
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const { data, isLoading, isError, error, refetch } = useAnalytics()
  const [spendMode, setSpendMode] = useState<"stacked" | "grouped">("stacked")

  const headline = useMemo(
    () =>
      data
        ? buildAnalyticsHeadline({
            categories: data.categoryBreakdown,
            incomeVsExpense: data.incomeVsExpense,
            months: data.months,
          })
        : null,
    [data],
  )

  if (isLoading) {
    return (
      <>
        <Header title="Analytics" />
        <PageWrapper>
          <AnalyticsSkeleton />
        </PageWrapper>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Header title="Analytics" />
        <PageWrapper>
          <ErrorState message={(error as Error)?.message} onRetry={refetch} />
        </PageWrapper>
      </>
    )
  }

  if (!data || data.trends.length === 0) {
    return (
      <>
        <Header title="Analytics" />
        <PageWrapper>
          <PhonePeriodPills className="mb-2" />
          <EmptyState
            title="No data to analyse"
            description="Add some transactions first and your spending trends will show up here. Upload a statement, import a CSV, or use the AI integration."
          />
        </PageWrapper>
      </>
    )
  }

  const { trends, categoryBreakdown, incomeVsExpense, ownerBreakdown, topMerchants } = data

  // ── Categorization flag + IO lookup (used in KPIs and chart) ─
  const hasCategorizedSpend = trends.some((t) => t.needs > 0 || t.wants > 0 || t.investments > 0)
  const ioByMonth = Object.fromEntries(incomeVsExpense.map((d) => [d.month, d]))

  // ── KPI computations ──────────────────────────────────────────
  const totalSpend = hasCategorizedSpend
    ? trends.reduce((s, t) => s + t.needs + t.wants, 0)
    : incomeVsExpense.reduce((s, d) => s + d.expense, 0)
  const totalInvested = trends.reduce((s, t) => s + t.investments, 0)
  const avgMonthly = trends.length > 0 ? Math.round(totalSpend / trends.length) : 0
  const netSurplus = incomeVsExpense.reduce((s, d) => s + d.surplus, 0)
  const avgSavingsRate =
    incomeVsExpense.length > 0
      ? Math.round(incomeVsExpense.reduce((s, d) => s + d.savingsRate, 0) / incomeVsExpense.length)
      : 0
  const peak = trends.reduce(
    (max, t) => {
      const val = hasCategorizedSpend ? t.needs + t.wants : (ioByMonth[t.month]?.expense ?? 0)
      return val > max.val ? { val, month: t.month } : max
    },
    { val: 0, month: "" },
  )

  // ── MoM comparison ────────────────────────────────────────────
  const recentMonths = incomeVsExpense.slice(-2)
  const [prevMonth, currMonth] = recentMonths.length === 2 ? recentMonths : [null, null]
  const pctChange = (curr: number, prev: number) =>
    prev > 0 ? Math.round(((curr - prev) / prev) * 100) : null
  const momComparisons =
    currMonth && prevMonth
      ? [
          { label: "Income", curr: currMonth.income, prev: prevMonth.income, invert: false },
          { label: "Expenses", curr: currMonth.expense, prev: prevMonth.expense, invert: true },
          {
            label: "Invested",
            curr: trends.at(-1)?.investments ?? 0,
            prev: trends.at(-2)?.investments ?? 0,
            invert: false,
          },
        ]
      : []

  // ── Chart data ────────────────────────────────────────────────
  const trendChart = trends.map((t) => ({
    month: formatMonthShort(t.month),
    needs: Math.round(t.needs),
    wants: Math.round(t.wants),
    investments: Math.round(t.investments),
    // Fallback when nothing is categorised: the raw expense total as one bar.
    spend: Math.round(ioByMonth[t.month]?.expense ?? 0),
  }))
  const ioChart = incomeVsExpense.map((d) => ({
    month: formatMonthShort(d.month),
    income: Math.round(d.income),
    expense: Math.round(d.expense),
  }))

  // ── Lists ─────────────────────────────────────────────────────
  const catData = categoryBreakdown.filter((c) => c.name && c.total > 0).slice(0, 12)
  const maxCat = catData[0]?.total || 1
  const catTotal = catData.reduce((s, c) => s + c.total, 0)
  const owners = Object.entries(ownerBreakdown)
  const bvaData = (data.budgetVsActual || []).filter((b) => b.budgeted > 0)
  const overBudgetCount = bvaData.filter((b) => b.actual > b.budgeted).length

  const stacked = spendMode === "stacked"
  const spendConfig = hasCategorizedSpend ? SPEND_CONFIG : SPEND_FALLBACK_CONFIG

  const spendChart = (
    <ChartCard
      data-testid="spending-trends"
      title="Spending trends"
      description={hasCategorizedSpend ? "Needs · Wants · Investments" : "Total spend"}
      height="lg"
      action={
        hasCategorizedSpend && (
          <Tabs value={spendMode} onValueChange={(v) => setSpendMode(v as typeof spendMode)}>
            <TabsList aria-label="Bar layout">
              <TabsTrigger value="stacked">Stacked</TabsTrigger>
              <TabsTrigger value="grouped">Grouped</TabsTrigger>
            </TabsList>
          </Tabs>
        )
      }
      legend={<CategoryLegend items={legendItemsFromConfig(spendConfig)} />}
      footer={
        !hasCategorizedSpend && (
          <p className="text-caption text-text-hint">
            Transactions are uncategorized — showing total spend. Categorize via MCP to see Needs /
            Wants / Investments breakdown.
          </p>
        )
      }
    >
      <ChartFrame config={spendConfig} height="lg">
        <BarChart data={trendChart} margin={CHART_MARGIN} barCategoryGap="28%" barGap={2}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="month" {...AXIS_PROPS} />
          <YAxis {...AXIS_PROPS} width={Y_AXIS_WIDTH} tickFormatter={fmtCompact} />
          <ChartTooltip content={<MoneyTooltip />} cursor={BAR_CURSOR} />
          {/* Bars as a flat array (not a Fragment): Recharts discovers chart
              children by type and does not recurse into Fragments (#59). */}
          {hasCategorizedSpend ? (
            SPEND_KEYS.map((k, i) => (
              <Bar
                key={k}
                dataKey={k}
                name={SPEND_CONFIG[k].label}
                fill={`var(--color-${k})`}
                stackId={stacked ? "a" : undefined}
                radius={stacked && i !== SPEND_KEYS.length - 1 ? 0 : BAR_RADIUS}
                isAnimationActive={false}
              />
            ))
          ) : (
            <Bar
              dataKey="spend"
              name="Spend"
              fill="var(--color-spend)"
              radius={BAR_RADIUS}
              isAnimationActive={false}
            />
          )}
        </BarChart>
      </ChartFrame>
    </ChartCard>
  )

  const ioChartCard = (
    <ChartCard
      data-testid="income-vs-expense"
      title="Income vs expense"
      description="Savings rate by month"
      height="lg"
      legend={<CategoryLegend items={legendItemsFromConfig(IO_CONFIG)} />}
      footer={
        <ul className="flex flex-wrap gap-1.5" aria-label="Savings rate by month">
          {incomeVsExpense.map((d) => (
            <li
              key={d.month}
              className={cn(
                "rounded-full px-2.5 py-0.5 font-mono text-caption font-medium",
                RATE_CHIP[rateTone(d.savingsRate)],
              )}
            >
              {formatMonthShort(d.month)} <RatioText>{`${d.savingsRate}%`}</RatioText>
            </li>
          ))}
        </ul>
      }
    >
      <ChartFrame config={IO_CONFIG} height="lg">
        <BarChart data={ioChart} margin={CHART_MARGIN} barCategoryGap="32%" barGap={3}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="month" {...AXIS_PROPS} />
          <YAxis {...AXIS_PROPS} width={Y_AXIS_WIDTH} tickFormatter={fmtCompact} />
          <ChartTooltip content={<MoneyTooltip showTotal={false} />} cursor={BAR_CURSOR} />
          <Bar
            dataKey="income"
            name="Income"
            fill="var(--color-income)"
            radius={BAR_RADIUS}
            isAnimationActive={false}
          />
          <Bar
            dataKey="expense"
            name="Expense"
            fill="var(--color-expense)"
            radius={BAR_RADIUS}
            isAnimationActive={false}
          />
        </BarChart>
      </ChartFrame>
    </ChartCard>
  )

  const categoryCard = (
    <SectionCard title="Category breakdown" data-testid="category-breakdown">
      <ul className="flex flex-col">
        {catData.map((cat) => {
          const pct = catTotal > 0 ? Math.round((cat.total / catTotal) * 100) : 0
          return (
            <li key={cat.slug || cat.name}>
              <Button
                variant="ghost"
                disabled={!cat.slug}
                aria-label={`${cat.name} — open category`}
                onClick={() =>
                  cat.slug &&
                  navigate({ to: "/analytics/categories/$slug", params: { slug: cat.slug } })
                }
                className="h-auto w-full flex-col items-stretch gap-1.5 px-2 py-2 text-left"
              >
                <span className="flex min-w-0 items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2">
                    {cat.icon && <span className="shrink-0 text-body">{cat.icon}</span>}
                    <span className="truncate text-body font-normal text-text-primary">
                      {cat.name}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="font-mono text-caption text-text-muted">
                      <RatioText>{`${pct}%`}</RatioText>
                    </span>
                    <Amount value={cat.total} className="text-body font-medium" />
                  </span>
                </span>
                <span aria-hidden className="block h-1 rounded-full bg-bg-elevated">
                  <RatioFill
                    as="span"
                    pct={(cat.total / maxCat) * 100}
                    className="block h-full rounded-full"
                    style={{
                      backgroundColor: CATEGORY_COLORS[cat.type || ""] || "var(--text-muted)",
                    }}
                  />
                </span>
              </Button>
            </li>
          )
        })}
      </ul>
    </SectionCard>
  )

  const merchantsCard = (
    <SectionCard title="Top merchants" data-testid="top-merchants">
      <ol className="flex flex-col">
        {topMerchants.slice(0, 10).map((m, i) => (
          <li key={m.id || m.name}>
            <Button
              variant="ghost"
              disabled={!m.id}
              aria-label={`${m.name} — open merchant`}
              onClick={() =>
                m.id && navigate({ to: "/analytics/merchants/$id", params: { id: m.id } })
              }
              className="h-auto w-full justify-start gap-3 px-2 py-2 text-left"
            >
              <span className="w-5 shrink-0 text-right font-mono text-caption text-text-hint">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="min-w-0 flex-1 truncate text-body font-normal text-text-primary">
                {m.name}
              </span>
              {m.isPerson && (
                <Badge variant="secondary" className="shrink-0">
                  person
                </Badge>
              )}
              <span className="flex shrink-0 items-baseline gap-1.5">
                <Amount value={m.total} className="text-body font-medium" />
                <span className="font-mono text-caption text-text-hint">{m.count}×</span>
              </span>
            </Button>
          </li>
        ))}
      </ol>
    </SectionCard>
  )

  return (
    <>
      <Header title="Analytics" />
      <PageWrapper>
        <div
          className="flex min-w-0 flex-col gap-4 md:gap-5"
          data-testid="analytics-page"
          data-layout={isDesktop ? "desktop" : "phone"}
        >
          {headline && (
            <AnswerHeadline headline={toSignetHeadline(headline)} renderValue={renderAnswerAmount}>
              {!isDesktop && <PhonePeriodPills />}
            </AnswerHeadline>
          )}
          {!headline && !isDesktop && <PhonePeriodPills />}

          <StatStrip
            isDesktop={isDesktop}
            items={[
              {
                label: "Total spend",
                value: <Amount value={totalSpend} />,
                meta: "Needs + wants across period",
              },
              {
                label: "Avg monthly",
                value: <Amount value={avgMonthly} />,
                meta: peak.month
                  ? `Peak ${fmtCompact(peak.val)} in ${formatMonthShort(peak.month)}`
                  : undefined,
              },
              {
                label: "Net surplus",
                value: <Amount value={netSurplus} />,
                meta: "Income − expenses",
                tone: netSurplus < 0 ? "warn" : "muted",
              },
              {
                label: "Savings rate",
                value: `${avgSavingsRate}%`,
                meta: `${fmtCompact(totalInvested)} invested`,
                tone: rateTone(avgSavingsRate),
              },
            ]}
          />

          {isDesktop ? (
            <div
              data-testid="analytics-charts-grid"
              className="grid grid-cols-1 gap-4 xl:grid-cols-2"
            >
              {spendChart}
              {ioChartCard}
            </div>
          ) : (
            <>
              {spendChart}
              {ioChartCard}
            </>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {categoryCard}
            {merchantsCard}
          </div>

          {bvaData.length > 0 && (
            <SectionCard
              title="Budget vs actual"
              data-testid="budget-vs-actual"
              action={
                overBudgetCount > 0 && (
                  <Badge variant="outline" className="border-accent-amber/40 text-accent-amber">
                    {overBudgetCount} over budget
                  </Badge>
                )
              }
            >
              <ul className="grid grid-cols-1 gap-x-6 gap-y-1 px-2 py-1 lg:grid-cols-2">
                {bvaData.map((b) => {
                  const isOver = b.actual > b.budgeted
                  const pct = Math.min((b.actual / b.budgeted) * 100, 100)
                  const atRisk = isOver || pct >= 80
                  const overagePct = isOver
                    ? Math.round(((b.actual - b.budgeted) / b.budgeted) * 100)
                    : 0
                  return (
                    <li key={b.slug || b.category} className="py-1.5">
                      <div className="mb-1.5 flex items-center justify-between gap-3">
                        <span className="flex min-w-0 items-center gap-2">
                          {b.icon && <span className="shrink-0 text-body">{b.icon}</span>}
                          <span className="truncate text-body text-text-primary">{b.category}</span>
                        </span>
                        <span className="flex shrink-0 items-baseline gap-1.5 text-caption">
                          {isOver && (
                            <span className="font-mono text-accent-amber">
                              <RatioText>{`+${overagePct}%`}</RatioText>
                            </span>
                          )}
                          <Amount value={b.actual} className="text-body font-medium" />
                          <span className="font-mono text-text-muted">
                            / <Amount value={b.budgeted} />
                          </span>
                        </span>
                      </div>
                      <RatioProgress
                        value={pct}
                        aria-label={`${b.category} budget used`}
                        className={cn(
                          "gap-0",
                          atRisk && "[&_[data-slot=progress-indicator]]:bg-accent-amber",
                        )}
                      />
                    </li>
                  )
                })}
              </ul>
            </SectionCard>
          )}

          {momComparisons.length > 0 && currMonth && prevMonth && (
            <SectionCard
              title={`${formatMonthShort(currMonth.month)} vs ${formatMonthShort(prevMonth.month)}`}
              data-testid="mom-comparison"
            >
              <div className="grid grid-cols-1 gap-2 p-2 sm:grid-cols-3 sm:gap-3">
                {momComparisons.map(({ label, curr, prev, invert }) => (
                  <div
                    key={label}
                    className="flex items-center justify-between gap-3 rounded-lg bg-bg-elevated p-3 sm:flex-col sm:items-start sm:p-4"
                  >
                    <p className="text-caption text-text-muted">{label}</p>
                    <div className="flex flex-col items-end gap-1 sm:items-start">
                      <Amount
                        value={curr}
                        className="text-callout font-semibold text-text-primary sm:text-title"
                      />
                      <span className="flex items-center gap-1.5">
                        <Change pct={pctChange(curr, prev)} invert={invert} />
                        <span className="text-caption text-text-muted">
                          vs <Amount value={prev} />
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {owners.length > 1 && (
            <SectionCard title="Spending by owner" data-testid="owner-breakdown">
              <div className="grid grid-cols-1 gap-3 p-2 sm:grid-cols-2 lg:grid-cols-3">
                {owners.map(([owner, o]) => {
                  const n = o.needs || 0
                  const w = o.wants || 0
                  const inv = o.investments || 0
                  const total = o.total || n + w + inv
                  const rows = [
                    { key: "needs", label: "Needs", val: n },
                    { key: "wants", label: "Wants", val: w },
                    { key: "investments", label: "Invested", val: inv },
                  ].map((r) => ({ ...r, pct: total > 0 ? (r.val / total) * 100 : 0 }))
                  const name = ownerLabels[owner] || owner
                  return (
                    <div key={owner} className="rounded-xl bg-bg-elevated p-4">
                      <div className="mb-3 flex items-center gap-2.5">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-bg-base text-caption font-semibold text-text-secondary ring-1 ring-border-default">
                          {name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-body font-medium text-text-primary">{name}</p>
                          <Amount value={total} className="text-caption text-text-muted" />
                        </div>
                      </div>
                      <div className="mb-3 flex h-2 gap-0.5 overflow-hidden rounded-full">
                        {rows
                          .filter((r) => r.pct > 0)
                          .map((r) => (
                            <RatioFill
                              key={r.key}
                              pct={r.pct}
                              style={{ backgroundColor: CATEGORY_COLORS[r.key] }}
                            />
                          ))}
                      </div>
                      <div className="flex flex-col gap-1">
                        {rows.map((r) => (
                          <div
                            key={r.key}
                            className="flex items-center justify-between text-caption"
                          >
                            <span className="flex items-center gap-1.5">
                              <span
                                className="size-1.5 rounded-full"
                                style={{ backgroundColor: CATEGORY_COLORS[r.key] }}
                              />
                              <span className="text-text-secondary">{r.label}</span>
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="font-mono text-text-muted">
                                <RatioText>{`${Math.round(r.pct)}%`}</RatioText>
                              </span>
                              <Amount value={r.val} className="text-text-primary" />
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </SectionCard>
          )}
        </div>
      </PageWrapper>
    </>
  )
}
